(function () {
    'use strict';

    /**
     * Controller for search UI interactions and loading states.
     * Applies to any container with [data-role="search-page"].
     * @namespace SearchController
     */
    const SearchController = {
        /**
         * Normalizes URL search parameters by sorting them alphabetically.
         * This allows consistent comparison of URLs regardless of parameter order.
         * 
         * @param {string} search - The search string from a URL (e.g., "?foo=bar&baz=qux")
         * @returns {string} Normalized query string with sorted parameters
         * @example
         * normalizeSearchParams("?b=2&a=1") // returns "a=1&b=2"
         */
        normalizeSearchParams(search) {
            const params = new URLSearchParams(search);
            const entries = Array.from(params.entries())
                .sort(([aKey], [bKey]) => aKey.localeCompare(bKey));
            return entries.map(([k, v]) => `${k}=${v}`).join('&');
        },
        
        /**
         * Checks if two URLs are functionally equal by comparing pathname and normalized query params.
         * Query parameters are compared order-independently.
         * 
         * @param {URL} url1 - First URL object to compare
         * @param {URL} url2 - Second URL object to compare
         * @returns {boolean} True if URLs have same pathname and equivalent query parameters
         */
        urlsAreEqual(url1, url2) {
            if (url1.pathname !== url2.pathname) return false;

            const norm1 = this.normalizeSearchParams(url1.search);
            const norm2 = this.normalizeSearchParams(url2.search);

            return norm1 === norm2;
        },
        
        /**
         * Initializes search interactions for all [data-role="search-page"] containers.
         * Attaches click handlers to facet links and filter links that:
         * - Skip showing spinner if clicking link to current page
         * - Skip showing spinner for modified clicks (Ctrl/Meta/Shift) or target="_blank"
         * - Display a full-screen loading spinner during navigation
         * - Remove spinner after 8 seconds if page hasn't loaded (network issue fallback)
         * 
         * @returns {void}
         */
        initialize() {
            document.querySelectorAll(
                '[data-role="search-page"] a.qld__search__facet-link:not([aria-disabled]), [data-role="search-page"] .qld__search__filters a'
            ).forEach(link => {
                link.addEventListener('click', e => {
                    if (link.target === '_blank' || e.ctrlKey || e.metaKey || e.shiftKey) {
                        return;
                    }
                    const targetUrl = new URL(link.href, window.location.origin);
                    const currentUrl = new URL(window.location.href);

                    if (this.urlsAreEqual(targetUrl, currentUrl)) {
                        return;
                    }

                    if (!document.getElementById('screenLoadingSpinner')) {
                        const spinner = document.createElement('div');
                        spinner.id = 'screenLoadingSpinner';
                        spinner.className = 'qld__loading_spinner';
                        spinner.setAttribute('role', 'status');
                        spinner.style.cssText = 'position:fixed;top:0;right:0;bottom:0;left:0;background-color: rgba(255,255,255,0.85);z-index:9999;';
                        spinner.innerHTML = `
                            <span class="qld__loading_spinner-wheel"></span>
                            <div class="qld__loading_spinner-label qld__display-md">Finding results...</div>`;
                        document.body.appendChild(spinner);

                        setTimeout(() => {
                            const s = document.getElementById('screenLoadingSpinner');
                            s?.remove();
                            console.log('Page did not reload. Possible network interruption occurred.');
                        }, 8000);
                    }
                });
            });
        }
    };


    // ============================================================================
    // Initialization
    // ============================================================================
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => SearchController.initialize());
    } else {
        SearchController.initialize();
    }
})();
